import React, { useState, useEffect } from 'react';
import { 
  Search, Package, Clock, CheckCircle, Truck, AlertCircle, XCircle,
  Eye, MapPin, Phone, Mail, RefreshCw, ChevronRight, User, CreditCard,
  ShoppingBag, Calendar, History, Loader2, CheckCircle2, PackageCheck,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ordersAPI } from '../services/api';

// Complete status configuration matching backend
const statusConfig = {
  'pending': { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Clock, label: 'Pending', next: ['payment_confirmed', 'cancelled'] },
  'payment_confirmed': { color: 'bg-blue-100 text-blue-700 border-blue-300', icon: CreditCard, label: 'Payment Confirmed', next: ['picking', 'cancelled'] },
  'picking': { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: ShoppingBag, label: 'Picking', next: ['picked', 'cancelled'] },
  'picked': { color: 'bg-orange-100 text-orange-700 border-orange-300', icon: PackageCheck, label: 'Picked', next: ['ready_for_delivery', 'ready_for_pickup', 'cancelled'] },
  'ready_for_delivery': { color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Package, label: 'Ready for Delivery', next: ['assigned', 'cancelled'] },
  'assigned': { color: 'bg-indigo-100 text-indigo-700 border-indigo-300', icon: User, label: 'Driver Assigned', next: ['driver_to_store', 'out_for_delivery', 'cancelled'] },
  'driver_to_store': { color: 'bg-cyan-100 text-cyan-700 border-cyan-300', icon: Truck, label: 'Driver En Route to Store', next: ['driver_at_store', 'cancelled'] },
  'driver_at_store': { color: 'bg-teal-100 text-teal-700 border-teal-300', icon: MapPin, label: 'Driver at Store', next: ['out_for_delivery', 'cancelled'] },
  'out_for_delivery': { color: 'bg-blue-100 text-blue-800 border-blue-400', icon: Truck, label: 'Out for Delivery', next: ['delivered', 'cancelled'] },
  'delivered': { color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle, label: 'Delivered', next: [] },
  'ready_for_pickup': { color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Package, label: 'Ready for Pickup', next: ['picked_up', 'cancelled'] },
  'picked_up': { color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle2, label: 'Picked Up', next: [] },
  'cancelled': { color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, label: 'Cancelled', next: [] },
};

// All possible statuses for filter
const allStatuses = [
  'pending', 'payment_confirmed', 'picking', 'picked', 
  'ready_for_delivery', 'assigned', 'driver_to_store', 'driver_at_store',
  'out_for_delivery', 'delivered', 'ready_for_pickup', 'picked_up', 'cancelled'
];

const AdminOrders = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showPaymentAlert, setShowPaymentAlert] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // Fetch orders from backend
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await ordersAPI.getAll();
      // Sort by date descending (newest first)
      const sortedOrders = (data || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setOrders(sortedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer_email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Update order status
  const handleUpdateStatus = async (orderId, newStatus) => {
    // Status changes that require payment confirmation
    const paymentRequiredStatuses = ['ready_for_pickup', 'ready_for_delivery', 'assigned', 'out_for_delivery'];
    
    // Find the order
    const order = orders.find(o => o.id === orderId);
    
    // Check if payment confirmation is needed
    if (paymentRequiredStatuses.includes(newStatus) && order?.status !== 'payment_confirmed') {
      // Check if order payment is not confirmed
      if (order?.payment_status !== 'paid' && order?.status === 'pending') {
        setPendingStatusChange({ orderId, newStatus });
        setShowPaymentAlert(true);
        return;
      }
    }
    
    await executeStatusUpdate(orderId, newStatus);
  };

  // Execute the actual status update
  const executeStatusUpdate = async (orderId, newStatus) => {
    try {
      setUpdating(true);
      await ordersAPI.updateStatus(orderId, newStatus);
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
      
      toast.success(`Order status updated to ${statusConfig[newStatus]?.label || newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update order status');
    } finally {
      setUpdating(false);
      setShowPaymentAlert(false);
      setPendingStatusChange(null);
    }
  };

  // Handle payment confirmation and proceed
  const handleConfirmPaymentAndProceed = async () => {
    if (pendingStatusChange) {
      // First update to payment_confirmed, then to the target status
      await executeStatusUpdate(pendingStatusChange.orderId, 'payment_confirmed');
      // Small delay then update to target status
      setTimeout(async () => {
        await executeStatusUpdate(pendingStatusChange.orderId, pendingStatusChange.newStatus);
      }, 500);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig['pending'];
    const StatusIcon = config.icon;
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <StatusIcon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Get allowed next statuses
  const getNextStatuses = (currentStatus) => {
    return statusConfig[currentStatus]?.next || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Order Management</h1>
          <p className="text-gray-500">Track and update order status from placement to delivery</p>
        </div>
        <Button 
          variant="outline" 
          className="flex items-center gap-2"
          onClick={fetchOrders}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Status Flow Guide */}
      <Card className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-purple-50">
        <p className="text-sm font-medium text-gray-700 mb-2">Order Status Flow:</p>
        <div className="flex flex-wrap items-center gap-1 text-xs">
          <Badge className="bg-gray-100">Pending</Badge>
          <ChevronRight className="w-3 h-3 text-gray-400" />
          <Badge className="bg-blue-100">Payment Confirmed</Badge>
          <ChevronRight className="w-3 h-3 text-gray-400" />
          <Badge className="bg-yellow-100">Picking</Badge>
          <ChevronRight className="w-3 h-3 text-gray-400" />
          <Badge className="bg-orange-100">Picked</Badge>
          <ChevronRight className="w-3 h-3 text-gray-400" />
          <span className="text-gray-500">(Delivery or Pickup)</span>
          <ChevronRight className="w-3 h-3 text-gray-400" />
          <Badge className="bg-green-100">Completed</Badge>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by order ID, customer name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="order-search-input"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]" data-testid="status-filter">
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {allStatuses.map(status => (
                <SelectItem key={status} value={status}>
                  {statusConfig[status]?.label || status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge className="bg-blue-100 text-blue-700 px-4 py-2">
            {filteredOrders.length} orders
          </Badge>
        </div>
      </Card>

      {/* Orders Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Order ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Total</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Update Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => {
                  const nextStatuses = getNextStatuses(order.status);
                  return (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-mono text-sm font-bold text-blue-700">
                          {order.order_number || `#${order.id?.slice(0, 8).toUpperCase()}`}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{order.customer_name || 'Guest'}</p>
                          <p className="text-xs text-gray-500">{order.customer_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-600">{formatDate(order.created_at)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">
                          {order.order_type === 'pickup' ? '🏪 Pickup' : '🚚 Delivery'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-800">${order.total?.toFixed(2) || '0.00'}</p>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-4 py-3">
                        {nextStatuses.length > 0 ? (
                          <Select 
                            onValueChange={(value) => handleUpdateStatus(order.id, value)}
                            disabled={updating}
                          >
                            <SelectTrigger className="w-[160px] text-xs h-8" data-testid={`update-status-${order.id}`}>
                              <SelectValue placeholder="→ Next Status" />
                            </SelectTrigger>
                            <SelectContent>
                              {nextStatuses.map(status => (
                                <SelectItem key={status} value={status}>
                                  {statusConfig[status]?.label || status}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Final status</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedOrder(order)}
                          className="text-blue-600 hover:text-blue-700"
                          data-testid={`view-order-${order.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedOrder(null)} />
          <Card className="relative z-10 w-full max-w-3xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            {/* Header with Order Number and QR Code */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-blue-700">
                  {selectedOrder.order_number || `#${selectedOrder.id?.slice(0, 8).toUpperCase()}`}
                </h2>
                <p className="text-gray-500 text-sm">{formatDate(selectedOrder.created_at)}</p>
                <div className="flex items-center gap-2 mt-2">
                  {getStatusBadge(selectedOrder.status)}
                  <Badge variant="outline" className="text-xs">
                    {selectedOrder.order_type === 'pickup' ? '🏪 Pickup' : '🚚 Delivery'}
                  </Badge>
                </div>
                {/* Pickup Code for store pickup orders */}
                {selectedOrder.order_type === 'pickup' && selectedOrder.pickup_code && (
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg inline-block">
                    <p className="text-xs text-amber-600 font-medium">Pickup Code</p>
                    <p className="text-2xl font-bold text-amber-700 tracking-widest">{selectedOrder.pickup_code}</p>
                  </div>
                )}
              </div>
              {/* QR Code */}
              {selectedOrder.qr_code && (
                <div className="flex-shrink-0 text-center">
                  <img 
                    src={selectedOrder.qr_code} 
                    alt="Order QR Code" 
                    className="w-24 h-24 border rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Scan for pickup/delivery</p>
                </div>
              )}
            </div>

            {/* Customer Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Customer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <p className="flex items-center gap-2 text-gray-600 text-sm">
                  <User className="w-4 h-4" />
                  {selectedOrder.customer_name || 'Guest'}
                </p>
                <p className="flex items-center gap-2 text-gray-600 text-sm">
                  <Mail className="w-4 h-4" />
                  {selectedOrder.customer_email || 'N/A'}
                </p>
                <p className="flex items-center gap-2 text-gray-600 text-sm">
                  <Phone className="w-4 h-4" />
                  {selectedOrder.customer_phone || 'N/A'}
                </p>
                <p className="flex items-center gap-2 text-gray-600 text-sm">
                  <Package className="w-4 h-4" />
                  {selectedOrder.order_type === 'pickup' ? 'Store Pickup' : 'Delivery'}
                </p>
              </div>
              {selectedOrder.order_type === 'delivery' && selectedOrder.delivery_address && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="flex items-start gap-2 text-gray-600 text-sm">
                    <MapPin className="w-4 h-4 mt-0.5" />
                    <span>
                      {selectedOrder.delivery_address.street}, {selectedOrder.delivery_address.city}, {selectedOrder.delivery_address.province} {selectedOrder.delivery_address.postal_code}
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Proof of Delivery */}
            {selectedOrder.proof_of_delivery && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Proof of Delivery
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {selectedOrder.proof_of_delivery.photo1_url && (
                    <img 
                      src={selectedOrder.proof_of_delivery.photo1_url} 
                      alt="Delivery Photo 1" 
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  )}
                  {selectedOrder.proof_of_delivery.photo2_url && (
                    <img 
                      src={selectedOrder.proof_of_delivery.photo2_url} 
                      alt="Delivery Photo 2" 
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  )}
                  {selectedOrder.proof_of_delivery.signature_url && (
                    <img 
                      src={selectedOrder.proof_of_delivery.signature_url} 
                      alt="Signature" 
                      className="w-full h-32 object-contain bg-white rounded-lg border p-2"
                    />
                  )}
                </div>
                <div className="mt-3 text-sm text-green-700">
                  <p><strong>Delivered by:</strong> {selectedOrder.proof_of_delivery.driver_name || 'Driver'}</p>
                  <p><strong>Delivered at:</strong> {formatDate(selectedOrder.proof_of_delivery.timestamp)}</p>
                  {selectedOrder.proof_of_delivery.notes && (
                    <p><strong>Notes:</strong> {selectedOrder.proof_of_delivery.notes}</p>
                  )}
                </div>
              </div>
            )}

            {/* Order Items */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Order Items ({selectedOrder.items?.length || 0})</h3>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {selectedOrder.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded">
                    <div className="flex items-center gap-3">
                      {item.image && (
                        <img src={item.image} alt={item.name} className="w-10 h-10 object-cover rounded" />
                      )}
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{item.name}</p>
                        <p className="text-xs text-gray-500">Qty: {item.quantity} × ${item.price?.toFixed(2)}</p>
                      </div>
                    </div>
                    <p className="font-semibold text-gray-800 text-sm">
                      ${(item.quantity * item.price).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
                <p className="font-bold text-gray-800">Total</p>
                <p className="text-xl font-bold text-gray-800">${selectedOrder.total?.toFixed(2) || '0.00'}</p>
              </div>
            </div>

            {/* Status History */}
            {selectedOrder.status_history && selectedOrder.status_history.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Status History
                </h3>
                <div className="space-y-2">
                  {selectedOrder.status_history.map((history, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span className="font-medium">
                        {statusConfig[history.status]?.label || history.status}
                      </span>
                      <span className="text-gray-400">—</span>
                      <span className="text-gray-500 text-xs">
                        {formatDate(history.timestamp)}
                      </span>
                      {history.notes && (
                        <span className="text-gray-400 text-xs italic">({history.notes})</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Update Status */}
            <div className="flex flex-wrap gap-4 pt-4 border-t">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">Update Order Status:</p>
                <div className="flex flex-wrap gap-2">
                  {getNextStatuses(selectedOrder.status).map(status => (
                    <Button
                      key={status}
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpdateStatus(selectedOrder.id, status)}
                      disabled={updating}
                      className={`${statusConfig[status]?.color} border`}
                    >
                      {updating ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        React.createElement(statusConfig[status]?.icon || CheckCircle, { className: "w-3 h-3 mr-1" })
                      )}
                      {statusConfig[status]?.label}
                    </Button>
                  ))}
                  {getNextStatuses(selectedOrder.status).length === 0 && (
                    <p className="text-sm text-gray-400 italic">This order has reached its final status.</p>
                  )}
                </div>
              </div>
              <Button variant="outline" onClick={() => setSelectedOrder(null)}>
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Payment Confirmation Alert Modal */}
      {showPaymentAlert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-4 border-b bg-amber-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-amber-900">Payment Confirmation Required</h2>
                  <p className="text-sm text-amber-700">Please verify payment before proceeding</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <p className="text-gray-700 mb-4">
                This order's payment has not been confirmed yet. Before proceeding to 
                <span className="font-semibold text-blue-600"> {statusConfig[pendingStatusChange?.newStatus]?.label}</span>, 
                please ensure the payment has been received.
              </p>
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Order:</span> #{pendingStatusChange?.orderId?.slice(-8)}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Current Status:</span> {orders.find(o => o.id === pendingStatusChange?.orderId)?.status}
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowPaymentAlert(false);
                    setPendingStatusChange(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleConfirmPaymentAndProceed}
                  disabled={updating}
                >
                  {updating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Confirm Payment & Proceed
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
